/**
 * Merges the user's civilian (skillsSurvey.occupationsDetails) and military
 * (militaryOccupations) work experience into a single list of
 * `{ onetCode, occupationKSAs }` entries — the input format expected by
 * `buildTfIdfOverlapSource`.
 *
 * Both inputs come from the same gateway query (`occupationListByOnetCodes`
 * on CPB), so their raw KSA shapes are identical: `{ ksa: { id }, importance, level }`.
 * The reducers store this raw shape as `rawOccupationKSAs` on both slices.
 * (PR #260 added rawOccupationKSAs preservation to the military reducer —
 * before that, military KSAs were filtered/flattened for UI display only
 * and the KSA-overlap math couldn't run on them.)
 *
 * Bug context: PR #258 introduced the KSA-overlap experience source but only
 * fed it from skillsSurvey.occupationsDetails. Military-only users (no
 * civilian work entered) got zero KSA-overlap signal — their entire MOS
 * crosswalk's KSA profile never reached the math. Symptom: Army-MOC + HS
 * user saw 19 cards / 0 GREATs regardless of MOS.
 *
 * Generic over the entry shape so the helper has zero app-specific imports
 * and can be unit-tested in isolation. The selector in `selectors.ts` calls
 * into this helper.
 */

interface RawOccupationKSA {
  ksa: { id: string };
  importance?: number | null;
}

interface OccupationEntry {
  onetCode?: string;
  rawOccupationKSAs?: RawOccupationKSA[] | null;
}

export interface MergedUserOccupation {
  onetCode: string;
  occupationKSAs: RawOccupationKSA[] | null | undefined;
}

export const mergeExperienceUserOccupations = (
  civilianOccupationsDetails: OccupationEntry[] | null | undefined,
  militaryOccupations: OccupationEntry[] | null | undefined
): MergedUserOccupation[] => {
  const result: MergedUserOccupation[] = [];
  for (const occ of civilianOccupationsDetails ?? []) {
    if (occ?.onetCode) {
      result.push({ onetCode: occ.onetCode, occupationKSAs: occ.rawOccupationKSAs });
    }
  }
  for (const occ of militaryOccupations ?? []) {
    if (occ?.onetCode) {
      result.push({ onetCode: occ.onetCode, occupationKSAs: occ.rawOccupationKSAs });
    }
  }
  return result;
};
